import { useState, useRef, useEffect } from 'react';

// A reload cue stays visible at least this long so a fast refresh/context-switch never flashes (E.14).
const RELOAD_MIN_HOLD = 500;

/**
 * Single phase source for every page's loader, so the pattern is identical on every page.
 *   showFirstLoad — first load, no data yet: the full-content, headerless loader.
 *   showBody      — a later reload (refresh / context switch / tab switch): the in-body loader.
 * The two are mutually exclusive — a first load can never turn into a reload — so the loader never
 * drops from the content-centre spot down into the body part-way through a first load.
 */
export default function usePageLoader(isLoading) {
    const [held, setHeld] = useState(isLoading);
    const loadedOnceRef = useRef(false);
    const startRef = useRef(isLoading ? Date.now() : 0);

    useEffect(() => {
        const hold = RELOAD_MIN_HOLD;
        if (isLoading) {
            if (!startRef.current) startRef.current = Date.now();
            if (!held) setHeld(true);
            return undefined;
        }
        if (!held) {
            // Not loading, no loader showing → the page already has content; future loads are reloads.
            loadedOnceRef.current = true;
            return undefined;
        }
        const remaining = Math.max(0, hold - (Date.now() - startRef.current));
        const timer = setTimeout(() => {
            loadedOnceRef.current = true;
            startRef.current = 0;
            setHeld(false);
        }, remaining);
        return () => clearTimeout(timer);
    }, [isLoading, held]);

    const busy = isLoading || held;
    return {
        showFirstLoad: busy && !loadedOnceRef.current,
        showBody: busy && loadedOnceRef.current,
    };
}
